import React from "react";
import loadable from "@loadable/component";
import { SectionContainer } from "@silversea-ssc/ui-components-react";

const PageBuilderOpeningText = loadable(
  () => import("./PageBuilderOpeningText")
);

const PageBuilderDescriptionText = loadable(
  () => import("./PageBuilderDescriptionText")
);
const PageBuilderOpeningHero = loadable(
  () => import("./PageBuilderOpeningHero")
);
const PageBuilderCollectionMosaic = loadable(
  () => import("./PageBuilderCollectionMosaic")
);
const PageBuilderPageSlider = loadable(() => import("./PageBuilderPageSlider"));
const PageBuilderCollectionSlider = loadable(
  () => import("./PageBuilderCollectionSlider")
);
const PageBuilderPageMosaic = loadable(() => import("./PageBuilderPageMosaic"));
const PageBuilderServiceRows = loadable(
  () => import("./PageBuilderServiceRows")
);
const PageBuilderTextBox = loadable(() => import("./PageBuilderTextBox"));
const PageBuilderFeaturedContentRows = loadable(
  () => import("./PageBuilderFeaturedContentRows")
);
const PageBuilderVideoSection = loadable(
  () => import("./PageBuilderVideoSection")
);
const PageBuilderFyc = loadable(() => import("./PageBuilderFyc"));
const PageBuilderDividerWithCTA = loadable(
  () => import("./PageBuilderDividerWithCTA")
);

const PageBuilderCruiseSlider = loadable(
  () => import("./PageBuilderCruiseSlider")
);
const PageBuilderOfferDescriptionRow = loadable(
  () => import("./PageBuilderOfferDescriptionRow")
);

const PageBuilderOpeningHeroSlider = loadable(
  () => import("./PageBuilderOpeningHeroSlider")
);

const PageBuilderCardMosaic = loadable(() => import("./PageBuilderCardMosaic"));
const PageBuilderOffersSelectionSlider = loadable(
  () => import("./PageBuilderOffersSelectionSlider")
);

const PageBuilderAccordionText = loadable(
  () => import("./PageBuilderAccordionText")
);
const PageBuilderOffersSelectionMosaic = loadable(
  () => import("./PageBuilderOffersSelectionMosaic")
);

const PageBuilderCustomOfferBox = loadable(
  () => import("./PageBuilderCustomOfferBox")
);

const PageBuilderCustomComponentContainer = loadable(
  () => import("./customComponents/CustomComponentContainer")
);
const PageBuilderCustomComponent = loadable(
  () => import("./customComponents/index")
);

import { ComponentBackgroundColor } from "types/componentBackground";
import {
  Maybe,
  PageBuilderCustomComponentFragment,
  PageBuilderCustomOfferBoxFragment,
  PageBuilderSectionsFragment,
  SanityFeaturedContentRows
} from "types/graphql-types";
import {
  getBackground,
  forceNextBackground,
  getCurrentOppositeColor,
  getCurrentBackground
} from "./componentBackgroundHelper";
import { WindowLocation } from "@reach/router";
import PageBuilderLocationSpecificTextBoxes from "./PageBuilderLocationSpecificTextBoxes";
import { PageBuilderCustomOfferBoxSectionContainer } from "./PageBuilderCustomOfferBox";

export type SanityBuilderSectionType = PageBuilderSectionsFragment;

export interface SanityBuilderToReactProps {
  sections: SanityBuilderSectionType[];
  initialBackgroundColor?: ComponentBackgroundColor;
  location: WindowLocation;
}

const SanityBuilderToComponents: React.FC<SanityBuilderToReactProps> = ({
  sections,
  initialBackgroundColor,
  location
}) => {
  if (initialBackgroundColor) forceNextBackground(initialBackgroundColor);

  return (
    <React.Fragment>
      {sections.map((section, index) => {
        return (
          <ComponentWithContainer
            key={section["_key"] || index}
            section={section}
            location={location}
          />
        );
      })}
    </React.Fragment>
  );
};

const ComponentWithContainer: React.FC<{
  section: SanityBuilderSectionType;
  location: WindowLocation;
}> = ({ section, location }) => {
  const { __typename } = section || {};
  let backgroundColor;

  switch (__typename) {
    case "SanityOpeningHeroSlider":
      forceNextBackground("white");
      return (
        <SanityBuilderSectionToComponent
          section={section}
          backgroundColor={backgroundColor}
          oppositeColor={getCurrentOppositeColor()}
          location={location}
        />
      );
    case "SanityOpeningHero":
      forceNextBackground("white");
      return (
        <SanityBuilderSectionToComponent
          section={section}
          backgroundColor={backgroundColor}
          oppositeColor={getCurrentOppositeColor()}
          location={location}
        />
      );
    case "SanityDescriptionText":
      forceNextBackground("neutral05");
      return (
        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor="white"
          dataContentName="description_text"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor={backgroundColor}
            oppositeColor={getCurrentOppositeColor()}
            location={location}
          />
        </SectionContainer>
      );

    case "SanityOpeningText":
      forceNextBackground("neutral05");
      return (
        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor="white"
          dataContentName="opening_text"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor={backgroundColor}
            oppositeColor={getCurrentOppositeColor()}
            location={location}
          />
        </SectionContainer>
      );

    case "SanityCollectionMosaic":
      forceNextBackground("white");
      return (
        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor="neutral05"
          dataContentName="collection_mosaic"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor={backgroundColor}
            oppositeColor={getCurrentOppositeColor()}
            location={location}
          />
        </SectionContainer>
      );

    case "SanityCardMosaic":
      forceNextBackground("white");
      return (
        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor="neutral05"
          dataContentName="card_mosaic"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor={backgroundColor}
            oppositeColor={getCurrentOppositeColor()}
            location={location}
          />
        </SectionContainer>
      );

    case "SanityCollectionSlider":
    case "SanityOffersSelectionSlider":
      backgroundColor = getBackground();
      return (
        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor={backgroundColor}
          dataContentName="collection_slider"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor={backgroundColor}
            oppositeColor={getCurrentOppositeColor()}
            location={location}
          />
        </SectionContainer>
      );
    case "SanityOffersSelectionMosaic":
      backgroundColor = getBackground();
      return (
        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor={backgroundColor}
          dataContentName="collection_mosaic"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor={backgroundColor}
            oppositeColor={getCurrentOppositeColor()}
            location={location}
          />
        </SectionContainer>
      );

    case "SanityPageSlider":
      backgroundColor = getBackground();
      return (
        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor={backgroundColor}
          dataContentName="page_slider"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor={backgroundColor}
            oppositeColor={getCurrentOppositeColor()}
            location={location}
          />
        </SectionContainer>
      );

    case "SanityCruiseSlider":
      return (
        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor="neutral10"
          dataContentName="cruise_slider"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor="neutral10"
            oppositeColor="white"
            location={location}
          />
        </SectionContainer>
      );

    case "SanityPageMosaic":
      forceNextBackground("white");
      return (
        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor="neutral05"
          dataContentName="page_mosaic"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor={backgroundColor}
            oppositeColor={getCurrentOppositeColor()}
            location={location}
          />
        </SectionContainer>
      );

    case "SanityServiceRows":
    case "SanityFeaturedContentRows":
      backgroundColor = getBackground();
      return (
        <SectionContainer
          spacingTop={
            (section as SanityFeaturedContentRows).heading
              ? "regular"
              : "compact"
          }
          spacingBottom={
            (section as SanityFeaturedContentRows).heading
              ? "regular"
              : "compact"
          }
          backgroundColor={backgroundColor}
          dataContentName="featured_content_rows"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor={backgroundColor}
            oppositeColor={getCurrentOppositeColor()}
            location={location}
          />
        </SectionContainer>
      );
    case "SanityTextBox":
      forceNextBackground("neutral05");
      return (
        <SectionContainer
          spacingTop="compact"
          spacingBottom="compact"
          backgroundColor="white"
          dataContentName="text_box"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor="white"
            oppositeColor="neutral05"
            location={location}
          />
        </SectionContainer>
      );

    case "SanityLocationSpecificTextBoxes":
      forceNextBackground("neutral05");
      return (
        <SectionContainer
          spacingTop="compact"
          spacingBottom="compact"
          backgroundColor="white"
          dataContentName="text_box"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor="white"
            oppositeColor="neutral05"
            location={location}
          />
        </SectionContainer>
      );

    case "SanityVideoSection":
      backgroundColor = getBackground();
      return (
        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor={backgroundColor}
          dataContentName="Video_section"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor={backgroundColor}
            oppositeColor={getCurrentOppositeColor()}
            location={location}
          />
        </SectionContainer>
      );
    case "SanityFyc":
      backgroundColor = getBackground();
      return (
        <SanityBuilderSectionToComponent
          section={section}
          backgroundColor={backgroundColor}
          oppositeColor={getCurrentOppositeColor()}
          location={location}
        />
      );

    case "SanityDividerWithCTA":
      //backgroundColor = getBackground();
      return (
        <SanityBuilderSectionToComponent
          section={section}
          backgroundColor={getCurrentBackground()}
          oppositeColor={getCurrentOppositeColor()}
          location={location}
        />
      );

    case "SanityOfferDescriptionRow":
      backgroundColor = getBackground();
      return (
        <SectionContainer
          spacingTop="compact"
          spacingBottom="compact"
          backgroundColor={backgroundColor}
          dataContentName="OfferDescriptionRow_section"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor={getCurrentBackground()}
            oppositeColor={getCurrentOppositeColor()}
            location={location}
          />
        </SectionContainer>
      );

    case "SanityAccordionText":
      backgroundColor = getBackground();
      return (
        <SectionContainer
          spacingTop="compact"
          spacingBottom="compact"
          backgroundColor={backgroundColor}
          dataContentName="AccordionText_section"
        >
          <SanityBuilderSectionToComponent
            section={section}
            backgroundColor={backgroundColor}
            oppositeColor={getCurrentOppositeColor()}
            location={location}
          />
        </SectionContainer>
      );

    case "SanityCustomOfferBox":
      backgroundColor = getBackground();
      return (
        <PageBuilderCustomOfferBoxSectionContainer
          backgroundColor={backgroundColor}
          section={section as PageBuilderCustomOfferBoxFragment}
        />
      );

    case "SanityCustomComponent":
      backgroundColor = getBackground();
      return (
        <PageBuilderCustomComponentContainer
          backgroundColor={backgroundColor}
          section={section as PageBuilderCustomComponentFragment}
        />
      );

    default:
      return <div>Error: {__typename}</div>;
  }
};

export const SanityBuilderSectionToComponent: React.FC<{
  section: SanityBuilderSectionType;
  backgroundColor?: ComponentBackgroundColor;
  oppositeColor?: ComponentBackgroundColor;
  location: WindowLocation;
}> = ({
  section,
  backgroundColor = undefined,
  oppositeColor = undefined,
  location
}) => {
  switch (section.__typename) {
    case "SanityOpeningHeroSlider":
      return <PageBuilderOpeningHeroSlider {...section} />;

    case "SanityOpeningHero":
      return <PageBuilderOpeningHero {...section} />;

    case "SanityDescriptionText":
      return <PageBuilderDescriptionText {...section} />;

    case "SanityOpeningText":
      return <PageBuilderOpeningText {...section} />;

    case "SanityOfferDescriptionRow":
      return (
        <PageBuilderOfferDescriptionRow
          {...section}
          location={location}
          cardBackgroundColor={oppositeColor}
        />
      );

    case "SanityCollectionMosaic":
      return <PageBuilderCollectionMosaic {...section} location={location} />;

    case "SanityCardMosaic":
      return <PageBuilderCardMosaic {...section} location={location} />;

    case "SanityOffersSelectionSlider":
      return (
        <PageBuilderOffersSelectionSlider
          {...section}
          cardBackgroundColor={oppositeColor}
          location={location}
        />
      );

    case "SanityOffersSelectionMosaic":
      return (
        <PageBuilderOffersSelectionMosaic {...section} location={location} />
      );

    case "SanityCollectionSlider":
      return (
        <PageBuilderCollectionSlider
          {...section}
          cardBackgroundColor={oppositeColor}
          location={location}
        />
      );

    case "SanityPageSlider":
      return (
        <PageBuilderPageSlider
          {...section}
          cardBackgroundColor={oppositeColor}
        />
      );

    case "SanityCruiseSlider":
      return <PageBuilderCruiseSlider {...section} />;

    case "SanityPageMosaic":
      return <PageBuilderPageMosaic {...section} />;

    case "SanityServiceRows":
      return (
        <PageBuilderServiceRows
          {...section}
          cardBackgroundColor={oppositeColor}
        />
      );

    case "SanityFeaturedContentRows":
      return <PageBuilderFeaturedContentRows {...section} />;

    case "SanityTextBox":
      return <PageBuilderTextBox {...section} />;

    case "SanityLocationSpecificTextBoxes":
      return <PageBuilderLocationSpecificTextBoxes {...section} />;

    case "SanityVideoSection":
      return <PageBuilderVideoSection {...section} />;

    case "SanityFyc":
      return <PageBuilderFyc {...section} location={location} />;

    case "SanityDividerWithCTA":
      return (
        <PageBuilderDividerWithCTA
          {...section}
          backgroundColorTop={backgroundColor}
          backgroundColorBottom={oppositeColor}
        />
      );

    case "SanityAccordionText":
      return (
        <PageBuilderAccordionText
          {...section}
          backgroundColor={oppositeColor}
        />
      );

    case "SanityCustomOfferBox":
      return (
        <PageBuilderCustomOfferBox
          {...section}
          cardBackgroundColor={oppositeColor}
        />
      );

    case "SanityCustomComponent":
      return (
        <PageBuilderCustomComponent
          {...section}
          cardBackgroundColor={oppositeColor}
        />
      );
  }
};

export const hasContent = (
  sections: Maybe<Maybe<SanityBuilderSectionType>[]> | undefined
): sections is SanityBuilderSectionType[] => {
  if (!sections) return false;
  return sections.length > 0;
};

export default React.memo(SanityBuilderToComponents);
